import { Form, InputNumber, Table } from 'antd';

import type { DayOfWeek } from 'app/typings/core/admin/enum/dayOfWeek';
import { DAY_OF_WEEK } from 'app/typings/core/admin/enum/dayOfWeek';

import styles from '../RoomDiscount.module.scss';

import { RevenueShare } from './RevenueShare';

const DAY_LABELS: Record<DayOfWeek, string> = {
  [DAY_OF_WEEK.MONDAY]: 'Mon',
  [DAY_OF_WEEK.TUESDAY]: 'Tue',
  [DAY_OF_WEEK.WEDNESDAY]: 'Wed',
  [DAY_OF_WEEK.THURSDAY]: 'Thu',
  [DAY_OF_WEEK.FRIDAY]: 'Fri',
  [DAY_OF_WEEK.SATURDAY]: 'Sat',
  [DAY_OF_WEEK.SUNDAY]: 'Sun',
};

type RowKey = 'discount' | 'revenue';

const REVENUE_SHARE_LABEL = 'Estimated room revenue share';

type RoomDiscountDaysTableProps = {
  currencyFormatter: (value: number) => string;
  defaultNightRoomDiscountDays?: Partial<Record<DayOfWeek, number>> | null;
  getDayBasePrice: (day: DayOfWeek) => number | null | undefined;
};

export const RoomDiscountDaysTable = ({
  currencyFormatter,
  defaultNightRoomDiscountDays,
  getDayBasePrice,
}: RoomDiscountDaysTableProps) => {
  const renderDayDiscount = (day: DayOfWeek) => {
    const dayValue = defaultNightRoomDiscountDays?.[day];
    const hasError = dayValue === null || dayValue === undefined;

    return (
      <div className={styles.dayDiscountInput}>
        <Form.Item
          noStyle
          name={['defaultNightRoomDiscountDays', day]}
          rules={[{ required: true, message: 'Required' }]}
        >
          <InputNumber
            min={0}
            max={100}
            controls={false}
            status={hasError ? 'error' : ''}
          />
        </Form.Item>
        <span className={styles.dayDiscountPercent}>%</span>
      </div>
    );
  };

  const renderRevenueShare = (day: DayOfWeek) => {
    const basePrice = getDayBasePrice(day);

    if (basePrice === null || basePrice === undefined) {
      return <span className={styles.originalPrice}>—</span>;
    }

    const discount = defaultNightRoomDiscountDays?.[day];

    if (discount === null || discount === undefined) {
      return <span className={styles.noPrice}>-</span>;
    }

    return (
      <div className={styles.dayRevenueShare}>
        <RevenueShare
          currencyFormatter={currencyFormatter}
          basePrice={basePrice}
          discountPercent={discount}
        />
      </div>
    );
  };

  const columns = [
    {
      title: '',
      dataIndex: 'label',
      key: 'label',
      className: styles.dayRowLabel,
      width: 200,
      render: (label: string) => (
        <span
          className={
            label === REVENUE_SHARE_LABEL ? styles.lowerLabelRow : undefined
          }
        >
          {label}
        </span>
      ),
    },
    ...Object.values(DAY_OF_WEEK).map((day) => ({
      title: DAY_LABELS[day],
      dataIndex: day,
      key: day.toLowerCase(),
      align: 'center' as const,

      width: 69,
      onCell: (record: { key: RowKey }) => ({
        className:
          record.key === 'discount'
            ? styles.dayInputCell
            : styles.dayRevenueCell,
      }),
      render: (_: unknown, record: { key: RowKey }) =>
        record.key === 'discount'
          ? renderDayDiscount(day)
          : renderRevenueShare(day),
    })),
  ];

  const hasMissingDailyDiscount = Object.values(DAY_OF_WEEK).some((day) => {
    const dayValue = defaultNightRoomDiscountDays?.[day];

    return dayValue === null || dayValue === undefined;
  });

  const dataSource: { key: RowKey; label: string }[] = [
    { key: 'discount', label: 'Room discount' },
    {
      key: 'revenue',
      label: REVENUE_SHARE_LABEL,
    },
  ];

  return (
    <>
      <div className={styles.daysTable}>
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          bordered
        />
      </div>
      {hasMissingDailyDiscount && (
        <span className={styles.daysError}>Discount required</span>
      )}
    </>
  );
};
